import { useCallback, useEffect, useState } from 'react'
import { getMyMembershipRights } from '@/client/sdk.gen'
import { useAuth } from '@/context'
import {
	hasPermission as checkPermission,
	type MembershipRightsRow,
	type PermissionScope,
	userCanCreateProject,
	userCanDeleteAnalysis,
	userCanDeleteArtifact,
	userCanDeleteProject,
	userCanProjectEdit,
	userCanWorkspaceAddMembers,
	userCanWorkspaceEditMembers,
	userCanWorkspaceRemoveMembers,
} from '@/utils/permissions'

let membershipRightsCache: { userId: string; memberships: MembershipRightsRow[] } | null = null
let membershipRightsVersion = 0
const membershipRightsListeners = new Set<() => void>()

function notifyMembershipRightsListeners(): void {
	for (const listener of membershipRightsListeners) {
		listener()
	}
}

/** Clear cache without bumping version (logout / auth failure). */
export function clearMembershipRightsCache(): void {
	membershipRightsCache = null
}

/** Clear cache and notify all hook consumers to refetch. */
export function invalidateMembershipRights(): void {
	membershipRightsCache = null
	membershipRightsVersion += 1
	notifyMembershipRightsListeners()
}

/**
 * Await a fresh membership-rights fetch, then bump version so all hook
 * consumers pick up the warm cache (e.g. after workspace create).
 */
export async function refreshMembershipRights(userId: string): Promise<void> {
	membershipRightsCache = null
	try {
		const res = await getMyMembershipRights()
		const rows = res.data?.memberships ?? []
		membershipRightsCache = { userId, memberships: rows }
	} catch {
		membershipRightsCache = { userId, memberships: [] }
	}
	membershipRightsVersion += 1
	notifyMembershipRightsListeners()
}

export function useMembershipRights() {
	const { user } = useAuth()
	const [memberships, setMemberships] = useState<MembershipRightsRow[] | undefined>(undefined)
	const [version, setVersion] = useState(membershipRightsVersion)

	useEffect(() => {
		const listener = () => setVersion(membershipRightsVersion)
		membershipRightsListeners.add(listener)
		return () => {
			membershipRightsListeners.delete(listener)
		}
	}, [])

	useEffect(() => {
		if (!user?.id) {
			setMemberships(undefined)
			return
		}

		if (membershipRightsCache?.userId === user.id) {
			setMemberships(membershipRightsCache.memberships)
			return
		}

		setMemberships(undefined)

		let cancelled = false
		;(async () => {
			try {
				const res = await getMyMembershipRights()
				if (cancelled) return
				const rows = res.data?.memberships ?? []
				membershipRightsCache = { userId: user.id, memberships: rows }
				setMemberships(rows)
			} catch {
				if (!cancelled) setMemberships([])
			}
		})()

		return () => {
			cancelled = true
		}
	}, [user?.id, version])

	const hasPermission = useCallback(
		(permission: string, scope?: PermissionScope) => checkPermission(memberships, permission, scope),
		[memberships]
	)

	return {
		memberships,
		hasPermission,
		canProjectEdit: (projectId: string, workspaceId?: string | null) =>
			userCanProjectEdit(memberships, projectId, workspaceId),
		canDeleteProject: (projectId: string, workspaceId?: string | null) =>
			userCanDeleteProject(memberships, projectId, workspaceId),
		canCreateProject: (workspaceId: string) => userCanCreateProject(memberships, workspaceId),
		canDeleteAnalysis: (projectId: string, workspaceId?: string | null) =>
			userCanDeleteAnalysis(memberships, projectId, workspaceId),
		canDeleteArtifact: (projectId: string, workspaceId?: string | null) =>
			userCanDeleteArtifact(memberships, projectId, workspaceId),
		canWorkspaceAddMembers: (workspaceId: string) =>
			userCanWorkspaceAddMembers(memberships, workspaceId),
		canWorkspaceEditMembers: (workspaceId: string) =>
			userCanWorkspaceEditMembers(memberships, workspaceId),
		canWorkspaceRemoveMembers: (workspaceId: string) =>
			userCanWorkspaceRemoveMembers(memberships, workspaceId),
	}
}
